 // ═══════════════════════════════════════════════════════════════════════════
// SERVICE OPEN-METEO - ET₀, Historique, Prévisions
// ═══════════════════════════════════════════════════════════════════════════
// frontend/src/services/meteoService.js
// Farm-Booking - Papy 73+ - Session 01/01/2026
// "L'eau juste, pas une goutte de perdue"
// ═══════════════════════════════════════════════════════════════════════════

// 📍 Coordonnées de la ferme (La Ferme du Petit Essart - Normandie)
const FERME_COORDS = {
  latitude: 49.3361,  // Arrondi pour Open-Meteo
  longitude: 1.0262,
  timezone: 'Europe/Paris',
  name: 'La Ferme du Petit Essart'
};

// ═══════════════════════════════════════════════════════════════════════════
// API ENDPOINTS OPEN-METEO (GRATUIT !)
// ═══════════════════════════════════════════════════════════════════════════

const ENDPOINTS = {
  forecast: 'https://api.open-meteo.com/v1/forecast',
  historical: 'https://archive-api.open-meteo.com/v1/archive'
};

// ═══════════════════════════════════════════════════════════════════════════
// PRÉVISIONS 7 JOURS + ET₀
// ═══════════════════════════════════════════════════════════════════════════

export const getMeteoForecast = async (coords = FERME_COORDS) => {
  try {
    const params = new URLSearchParams({
      latitude: coords.latitude,
      longitude: coords.longitude,
      timezone: coords.timezone,
      // Données journalières - paramètres fiables
      daily: [
        'et0_fao_evapotranspiration',  // 🎯 LE GRAAL : ET₀ FAO-56
        'precipitation_sum',
        'precipitation_probability_max',
        'temperature_2m_max',
        'temperature_2m_min',
        'windspeed_10m_max'
      ].join(','),
      forecast_days: 7
    });

    const response = await fetch(`${ENDPOINTS.forecast}?${params}`);
    
    if (!response.ok) {
      throw new Error(`Open-Meteo erreur: ${response.status}`);
    }

    const data = await response.json();
    return formatForecastData(data);

  } catch (error) {
    console.error('Erreur getMeteoForecast:', error);
    throw error;
  }
};

// ═══════════════════════════════════════════════════════════════════════════
// HISTORIQUE MÉTÉO (pour analyse rétrospective)
// ═══════════════════════════════════════════════════════════════════════════

export const getMeteoHistorique = async (dateDebut, dateFin, coords = FERME_COORDS) => {
  try {
    // Exactement les mêmes paramètres que le curl qui fonctionne
    const params = new URLSearchParams({
      latitude: coords.latitude,
      longitude: coords.longitude,
      timezone: coords.timezone,
      start_date: dateDebut,  // Format: YYYY-MM-DD
      end_date: dateFin,
      daily: 'et0_fao_evapotranspiration,precipitation_sum,temperature_2m_max,temperature_2m_min'
    });

    console.log('📡 Appel Open-Meteo Archive:', `${ENDPOINTS.historical}?${params}`);

    const response = await fetch(`${ENDPOINTS.historical}?${params}`);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Erreur Open-Meteo:', response.status, errorText);
      throw new Error(`Open-Meteo Archive erreur: ${response.status}`);
    }

    const data = await response.json();
    console.log('✅ Données reçues:', data.daily?.time?.length, 'jours');
    return formatHistoriqueData(data);

  } catch (error) {
    console.error('Erreur getMeteoHistorique:', error);
    throw error;
  }
};

// ═══════════════════════════════════════════════════════════════════════════
// CALCUL BILAN HYDRIQUE POUR UNE CULTURE
// ═══════════════════════════════════════════════════════════════════════════

export const calculerBilanHydrique = async (dateSemis, dateFinOuAujourdhui, stades, coords = FERME_COORDS) => {
  try {
    // Calculer la durée totale du cycle
    const cycleTotalJours = stades.reduce((sum, s) => sum + s.duree, 0);
    
    // Calculer la date de fin du cycle (semis + durée cycle)
    const dateDebutObj = new Date(dateSemis);
    const dateFinCycleObj = new Date(dateDebutObj);
    dateFinCycleObj.setDate(dateFinCycleObj.getDate() + cycleTotalJours);
    
    // La date de fin est le minimum entre : aujourd'hui, date demandée, ou fin du cycle
    const aujourdhuiObj = new Date();
    const dateFinDemandeObj = new Date(dateFinOuAujourdhui);
    
    // Prendre la date la plus ancienne des trois
    let dateFinEffectiveObj = dateFinDemandeObj;
    if (dateFinCycleObj < dateFinEffectiveObj) dateFinEffectiveObj = dateFinCycleObj;
    if (aujourdhuiObj < dateFinEffectiveObj) dateFinEffectiveObj = aujourdhuiObj;
    
    // Vérifier que la date de début n'est pas dans le futur
    if (dateDebutObj > aujourdhuiObj) {
      console.log('⚠️ Date de semis dans le futur, pas d\'historique disponible');
      return null;
    }
    
    const dateFin = dateFinEffectiveObj.toISOString().split('T')[0];
    
    console.log(`📊 Bilan hydrique: ${dateSemis} → ${dateFin} (cycle ${cycleTotalJours}j)`);
    
    // Récupérer l'historique météo
    const meteoData = await getMeteoHistorique(dateSemis, dateFin, coords);
    
    if (!meteoData || meteoData.length === 0) {
      console.log('⚠️ Pas de données météo pour cette période');
      return null;
    }
    
    // Calculer ETc jour par jour selon le stade
    const bilanJournalier = [];
    let jourCulture = 0;
    let cumulETc = 0;
    let cumulPluie = 0;
    let stadeActuelIndex = 0;
    let joursStadeAccumules = 0;

    for (const jour of meteoData) {
      jourCulture++;
      
      // Déterminer le stade actuel
      while (stadeActuelIndex < stades.length - 1 && 
             jourCulture > joursStadeAccumules + stades[stadeActuelIndex].duree) {
        joursStadeAccumules += stades[stadeActuelIndex].duree;
        stadeActuelIndex++;
      }
      
      const stadeActuel = stades[stadeActuelIndex];
      const kc = stadeActuel.kc;
      
      // Calcul ETc = ET₀ × Kc
      const et0 = jour.et0 || 0;
      const etc = et0 * kc;
      const pluie = jour.precipitation || 0;
      const deficit = etc - pluie;

      cumulETc += etc;
      cumulPluie += pluie;

      bilanJournalier.push({
        date: jour.date,
        jourCulture,
        stade: stadeActuel.code,
        stadeNom: stadeActuel.nom,
        kc,
        et0: Math.round(et0 * 100) / 100,
        etc: Math.round(etc * 100) / 100,
        pluie: Math.round(pluie * 100) / 100,
        deficit: Math.round(deficit * 100) / 100,
        cumulETc: Math.round(cumulETc * 100) / 100,
        cumulPluie: Math.round(cumulPluie * 100) / 100,
        cumulDeficit: Math.round((cumulETc - cumulPluie) * 100) / 100,
        tempMax: jour.tempMax,
        tempMin: jour.tempMin
      });
    }

    // Résumé par stade
    const resumeParStade = stades.map(stade => {
      const joursStade = bilanJournalier.filter(j => j.stade === stade.code);
      return {
        code: stade.code,
        nom: stade.nom,
        kc: stade.kc,
        nbJours: joursStade.length,
        totalET0: Math.round(joursStade.reduce((sum, j) => sum + j.et0, 0) * 10) / 10,
        totalETc: Math.round(joursStade.reduce((sum, j) => sum + j.etc, 0) * 10) / 10,
        totalPluie: Math.round(joursStade.reduce((sum, j) => sum + j.pluie, 0) * 10) / 10,
        deficit: Math.round(joursStade.reduce((sum, j) => sum + j.deficit, 0) * 10) / 10
      };
    });

    return {
      dateSemis,
      dateFin: dateFinOuAujourdhui,
      nbJours: bilanJournalier.length,
      totalETc: Math.round(cumulETc * 10) / 10,
      totalPluie: Math.round(cumulPluie * 10) / 10,
      deficitTotal: Math.round((cumulETc - cumulPluie) * 10) / 10,
      besoinArrosage: Math.max(0, Math.round((cumulETc - cumulPluie) * 10) / 10),
      bilanJournalier,
      resumeParStade
    };

  } catch (error) {
    console.error('Erreur calculerBilanHydrique:', error);
    throw error;
  }
};

// ═══════════════════════════════════════════════════════════════════════════
// PRÉVISION BESOINS EAU 7 JOURS
// ═══════════════════════════════════════════════════════════════════════════

export const prevoir7Jours = async (jourCultureActuel, stades, coords = FERME_COORDS) => {
  try {
    const forecast = await getMeteoForecast(coords);
    
    // Déterminer stade actuel
    let stadeIndex = 0;
    let joursAccumules = 0;
    while (stadeIndex < stades.length - 1 && 
           jourCultureActuel > joursAccumules + stades[stadeIndex].duree) {
      joursAccumules += stades[stadeIndex].duree;
      stadeIndex++;
    }

    const previsions = forecast.daily.map((jour, idx) => {
      // Avancer dans les stades si besoin
      const jourFutur = jourCultureActuel + idx;
      while (stadeIndex < stades.length - 1 && 
             jourFutur > joursAccumules + stades[stadeIndex].duree) {
        joursAccumules += stades[stadeIndex].duree;
        stadeIndex++;
      }
      
      const stade = stades[stadeIndex];
      const etc = jour.et0 * stade.kc;
      
      return {
        date: jour.date,
        jourCulture: jourFutur,
        stade: stade.code,
        kc: stade.kc,
        et0: Math.round(jour.et0 * 100) / 100,
        etc: Math.round(etc * 100) / 100,
        pluiePrevue: jour.precipitation,
        probaPluie: jour.precipitationProbability,
        deficit: Math.round((etc - jour.precipitation) * 100) / 100,
        tempMax: jour.tempMax,
        tempMin: jour.tempMin,
        tempSol: jour.soilTemp,
        humiditeSol: jour.soilMoisture,
        alerte: etc - jour.precipitation > 3 ? 'arrosage_recommande' : null
      };
    });

    const totalETcPrevu = previsions.reduce((sum, j) => sum + j.etc, 0);
    const totalPluiePrevue = previsions.reduce((sum, j) => sum + j.pluiePrevue, 0);

    return {
      previsions,
      totalETcPrevu: Math.round(totalETcPrevu * 10) / 10,
      totalPluiePrevue: Math.round(totalPluiePrevue * 10) / 10,
      besoinArrosage7j: Math.max(0, Math.round((totalETcPrevu - totalPluiePrevue) * 10) / 10),
      joursArrosageRecommande: previsions.filter(j => j.alerte).length
    };

  } catch (error) {
    console.error('Erreur prevoir7Jours:', error);
    throw error;
  }
};

// ═══════════════════════════════════════════════════════════════════════════
// FORMATAGE DES DONNÉES
// ═══════════════════════════════════════════════════════════════════════════

const formatForecastData = (data) => {
  const daily = data.daily.time.map((date, i) => ({
    date,
    et0: data.daily.et0_fao_evapotranspiration[i],
    precipitation: data.daily.precipitation_sum[i] || 0,
    precipitationProbability: data.daily.precipitation_probability_max?.[i] || 0,
    tempMax: data.daily.temperature_2m_max[i],
    tempMin: data.daily.temperature_2m_min[i],
    windMax: data.daily.windspeed_10m_max?.[i] || 0
  }));

  return {
    latitude: data.latitude,
    longitude: data.longitude,
    timezone: data.timezone,
    daily
  };
};

const formatHistoriqueData = (data) => {
  return data.daily.time.map((date, i) => ({
    date,
    et0: data.daily.et0_fao_evapotranspiration[i] || 0,
    precipitation: data.daily.precipitation_sum[i] || 0,
    tempMax: data.daily.temperature_2m_max[i],
    tempMin: data.daily.temperature_2m_min[i]
  }));
};

// ═══════════════════════════════════════════════════════════════════════════
// EXPORT PAR DÉFAUT
// ═══════════════════════════════════════════════════════════════════════════

const meteoService = {
  getMeteoForecast,
  getMeteoHistorique,
  calculerBilanHydrique,
  prevoir7Jours,
  FERME_COORDS
};

export default meteoService;